import { useEffect } from 'react';

import { Box } from 'components/Box/Box';
import { VolopDetailsList } from 'components/DetailsList/Volop/VolopDetailsList';
import { PageToolbar } from 'components/PageToolbar/PageToolbar';
import { PrintHidden } from 'components/PrintHidden/PrintHidden';
import { useUser } from 'contexts/UserEnvironmentContext';
import { useSetDocumentTitle } from 'hooks/useSetDocumentTitle';
import type { ApiVolop } from 'modules/listing/api/volop/types/ApiVolop';
import { ListingViewAdminTools } from 'modules/listing/components/ListingView/AdminTools/ListingViewAdminTools';
import { ListingViewBody } from 'modules/listing/components/ListingView/Body/ListingViewBody';
import { ListingViewFooter } from 'modules/listing/components/ListingView/Footer/ListingViewFooter';
import { ListingViewHeader } from 'modules/listing/components/ListingView/Header/ListingViewHeader';
import { ListingViewHeaderSearchContext } from 'modules/listing/components/ListingView/HeaderSearchContext/ListingViewHeaderSearchContext';
import { ListingViewMain } from 'modules/listing/components/ListingView/Main/ListingViewMain';
import { ListingViewSection } from 'modules/listing/components/ListingView/Section/ListingViewSection';
import { ListingViewSidebar } from 'modules/listing/components/ListingView/Sidebar/ListingViewSidebar';
import { ListingViewSidebarContent } from 'modules/listing/components/ListingView/Sidebar/ListingViewSidebarContent';
import { ListingViewSidebarImage } from 'modules/listing/components/ListingView/Sidebar/ListingViewSidebarImage';
import { ListingViewSidebarLinks } from 'modules/listing/components/ListingView/Sidebar/ListingViewSidebarLinks';
import { ListingViewSidebarTitle } from 'modules/listing/components/ListingView/Sidebar/ListingViewSidebarTitle';
import { ListingViewSignupCta } from 'modules/listing/components/ListingView/SignupCta/ListingViewSignupCta';
import { ListingViewDescriptionSection } from 'modules/listing/components/ListingView/sections/Description/ListingViewDescriptionSection';
import { ListingViewApplySection } from 'modules/listing/components/ListingView/sections/ListingViewApplySection';
import { ListingViewLocationSection } from 'modules/listing/components/ListingView/sections/ListingViewLocationSection';
import { ViewVolopActionButtons } from 'modules/listing/volops/components/ActionButtons/ViewVolopActionButtons';
import { SearchContextHeader } from 'modules/search/components/Context/SearchContextHeader';
import { useSearchContextHeader } from 'modules/search/containers/SearchContextHeader/useSearchContextHeader';
import { trackClicked } from 'utils/analytics/track/trackClicked';
import { trackListing } from 'utils/analytics/trackListing';
import { isUserOrgOwner } from 'utils/listings/getOrgOwner';
import { isExternalURL } from 'utils/url/isExternalURL';
import { showModal } from 'zustand-stores/modalStore';

type Props = {
  volop: ApiVolop;
};

export function ViewVolopPage({ volop }: Props) {
  const {
    address,
    applyText,
    applyTextTruncated,
    applyUrl,
    applyEmail,
    areasOfFocus,
    id,
    hasAts,
    startDate,
    directions,
    endDate,
    startsLocal,
    endsLocal,
    functions,
    requestedAttachments,
    requestedAttachmentsHash,
    firstPublished,
    driversLicenseRequired,
    backgroundCheckRequired,
    orientationRequired,
    feeRequired,
    feeAmount,
    isPostedAnonymously,
    isRecurring,
    name,
    timezone,
    welcomeFamilies,
    welcomeTeens,
    welcomeGroups,
    welcomeAge55Plus,
    welcomeIntl,
    welcomePrivateCorpGroups,
    detailsStipendProvided,
    detailsCulturalSupport,
    detailsHousingAvailable,
    detailsTrainingProvided,
    detailsAcademicCreditAvailable,
    detailsWheelchairAccessible,
    timesOfDay,
    type,
    image,
    description,
    descriptionTruncated,
    expectedTime,
    remoteOptions,
    url,
    org,
    ageRequirement,
    otherRequirements,
    numVolunteersNeeded,
    commitmentDetails,
    canBeDoneInADay,
  } = volop;

  const { searchContext } = useSearchContextHeader({ currentObjectId: id });

  const { user } = useUser();
  const isOrgOwner = isUserOrgOwner(user, org?.id);

  useSetDocumentTitle(name);

  useEffect(() => {
    trackListing({
      status: 'published',
      hasAts,
      listingType: type,
      listingId: id,
      listingName: name,
      orgId: org?.id,
      orgName: org?.name,
      orgType: org?.orgType,
    });
  }, [hasAts, id, name, org?.id, org?.name, org?.orgType, type]);

  return (
    <>
      {isOrgOwner && org && (
        <PageToolbar
          centered
          message={getText(
            'Your organization posted this volunteer opportunity',
          )}
          tools={
            <ListingViewAdminTools
              listingId={id}
              listingType="VOLOP"
              orgId={org.id}
              canPost={Boolean(
                user?.orgs.find((o) => o.id === org.id)?.canPostVolops,
              )}
              hasAts={hasAts}
            />
          }
        />
      )}

      <ListingViewBody>
        <ListingViewSidebar maxWidth={288}>
          <ListingViewSidebarImage
            isPostedAnonymously={isPostedAnonymously}
            orgLogo={org?.logo}
            orgName={isPostedAnonymously ? '' : org?.name}
            orgURLForCurrentLocale={
              org?.url?.[CURRENT_LOCALE()]
                ? org?.url[CURRENT_LOCALE()]
                : undefined
            }
          />
          <ListingViewSidebarTitle
            address={address}
            listingName={name}
            listingType="VOLOP"
            orgName={isPostedAnonymously ? '' : org?.name}
            orgUrl={isPostedAnonymously ? null : org?.url}
            remoteOptions={remoteOptions}
          />
          <ListingViewSidebarContent
            firstPublished={firstPublished}
            listingId={id}
            listingType={type}
            hasAts={hasAts}
            isExpired={false}
          />
          <ListingViewSidebarLinks
            searchContext={searchContext}
            orgUrl={isPostedAnonymously ? null : org?.url}
          />
        </ListingViewSidebar>

        <ListingViewMain>
          {searchContext && (
            <ListingViewHeaderSearchContext searchContext={searchContext} />
          )}

          <ListingViewHeader
            address={address}
            isPostedAnonymously={isPostedAnonymously}
            listingId={id}
            listingName={name}
            listingType={type}
            listingFirstPublished={firstPublished}
            orgName={isPostedAnonymously ? '' : org?.name}
            orgType={org?.orgType}
            orgUrl={isPostedAnonymously ? null : org?.url}
            remoteOptions={remoteOptions}
            canBeDoneInADay={canBeDoneInADay}
            isExpired={false}
            actionButtons={<ViewVolopActionButtons volop={volop} />}
          />

          <ListingViewSection title={getText('Details')} noBorderTop>
            <VolopDetailsList
              areasOfFocus={areasOfFocus}
              startDate={startDate}
              endDate={endDate}
              startsLocal={startsLocal}
              endsLocal={endsLocal}
              driversLicenseRequired={driversLicenseRequired}
              backgroundCheckRequired={backgroundCheckRequired}
              orientationRequired={orientationRequired}
              feeRequired={feeRequired}
              feeAmount={feeAmount}
              isRecurring={isRecurring}
              timezone={timezone}
              welcomeFamilies={welcomeFamilies}
              welcomeTeens={welcomeTeens}
              welcomeGroups={welcomeGroups}
              welcomeAge55Plus={welcomeAge55Plus}
              welcomeIntl={welcomeIntl}
              welcomePrivateCorpGroups={welcomePrivateCorpGroups}
              detailsStipendProvided={detailsStipendProvided}
              detailsTrainingProvided={detailsTrainingProvided}
              detailsHousingAvailable={detailsHousingAvailable}
              detailsCulturalSupport={detailsCulturalSupport}
              detailsAcademicCreditAvailable={detailsAcademicCreditAvailable}
              detailsWheelchairAccessible={detailsWheelchairAccessible}
              timesOfDay={timesOfDay}
              expectedTime={expectedTime}
              ageRequirement={ageRequirement}
              otherRequirements={otherRequirements}
              numVolunteersNeeded={numVolunteersNeeded}
              commitmentDetails={commitmentDetails}
            />
          </ListingViewSection>

          <ListingViewDescriptionSection
            listingName={name}
            image={image}
            description={description}
            descriptionTruncated={descriptionTruncated}
            handleShowEmailModal={(link) => {
              if (!user && isExternalURL(link.href)) {
                showModal('EMAIL_ALERT', {
                  listingFunctions: functions,
                  orgId: org?.id,
                  listingName: name,
                  listingType: 'VOLOP',
                });
              }
            }}
            showMoreLinkTitle={getText(
              'Read more about this volunteer opportunity',
            )}
            onToggleDetails={(isOpen) => {
              if (!isOpen) return;

              trackClicked('read more about this listing', {
                listing_type: volop.type,
                locale: volop.locale,
              });
            }}
          />

          <ListingViewLocationSection
            listingType={type}
            address={address}
            directions={directions}
            remoteOptions={remoteOptions}
          />

          <ListingViewApplySection
            applyEmail={applyEmail}
            applyText={applyText}
            applyTextTruncated={applyTextTruncated}
            applyUrl={applyUrl}
            functions={functions}
            hasAts={hasAts}
            listingName={name}
            listingType="VOLOP"
            listingId={id}
            orgType={org?.orgType}
            orgId={org?.id}
            requestedAttachments={requestedAttachments}
            requestedAttachmentsHash={requestedAttachmentsHash}
            synonym={null}
          />

          {searchContext && (
            <PrintHidden>
              <Box mt={70} mb={48}>
                <SearchContextHeader searchContext={searchContext} noDivider />
              </Box>
            </PrintHidden>
          )}
        </ListingViewMain>
      </ListingViewBody>

      {!user && (
        <PrintHidden>
          <ListingViewFooter>
            <ListingViewSignupCta
              listingUrl={url[CURRENT_LOCALE()]}
              type={type}
            />
          </ListingViewFooter>
        </PrintHidden>
      )}
    </>
  );
}
